import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import {
  Calendar,
  fromDateId,
  toDateId,
} from '@marceloterreiro/flash-calendar';
import i18n from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Icon } from '~/global/components/icon/Icon';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { TriggerRef } from '@rn-primitives/popover';
import { addMonths, endOfMonth, subMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { calendarTheme } from '@/lib/calendar-theme';
import { DatePickerTrigger, type DatePickerProps } from './DatePickerTrigger';

export type { DatePickerProps, DatePickerVariant } from './DatePickerTrigger';

export const DatePicker = ({
  value,
  onChange,
  variant = 'card',
  label,
  disabled = false,
  minimumDate,
  className,
  testID,
}: DatePickerProps) => {
  const { t } = useTranslation('calendar');
  const triggerRef = useRef<TriggerRef>(null);
  const [monthId, setMonthId] = useState(() => toDateId(value));

  useEffect(() => {
    setMonthId(toDateId(value));
  }, [value]);

  const minDateId = useMemo(
    () => (minimumDate ? toDateId(minimumDate) : undefined),
    [minimumDate],
  );

  const handleCalendarDayPress = useCallback(
    (dateString: string) => {
      const picked = fromDateId(dateString);
      // Defensive: flash-calendar already disables days before calendarMinDateId.
      if (minimumDate && picked < minimumDate) return;
      onChange(picked);
      triggerRef.current?.close();
    },
    [onChange, minimumDate],
  );

  // Don't let the user page to a month that lies entirely before minimumDate.
  const canGoPrevMonth = useMemo(
    () =>
      !minimumDate ||
      endOfMonth(subMonths(fromDateId(monthId), 1)) >= minimumDate,
    [minimumDate, monthId],
  );

  const handleNextMonth = () =>
    setMonthId((prev) => toDateId(addMonths(fromDateId(prev), 1)));
  const handlePrevMonth = () => {
    if (!canGoPrevMonth) return;
    setMonthId((prev) => toDateId(subMonths(fromDateId(prev), 1)));
  };

  const getCalendarDayFormat = useCallback(
    (date: Date) => String(date.getDate()),
    [],
  );

  const calendarActiveDateRanges = useMemo(() => {
    const dateId = toDateId(value);
    return [{ startId: dateId, endId: dateId }];
  }, [value]);

  return (
    <Popover>
      <PopoverTrigger ref={triggerRef} asChild>
        <Pressable disabled={disabled} className={disabled ? 'opacity-50' : ''}>
          <DatePickerTrigger
            value={value}
            variant={variant}
            label={label}
            className={className}
            testID={testID}
          />
        </Pressable>
      </PopoverTrigger>
      <PopoverContent className="max-w-[calc(100vw-2rem)] p-0">
        {/*
         * `date-picker-calendar` scopes day lookups in e2e: inside it the only
         * numeric text is the day cells, so a day is addressable by its number
         * (locale-independent — `getCalendarDayFormat` renders `getDate()`).
         */}
        <View
          testID="date-picker-calendar"
          collapsable={false}
          className="p-3"
          pointerEvents="auto"
        >
          <View className="flex flex-row items-center justify-between mb-3">
            <Text className="font-medium">
              {t('selectDate', { defaultValue: 'Select date' })}
            </Text>
            <View className="flex flex-row items-center gap-x-2">
              <Button
                testID="date-picker-prev-month"
                variant="ghost"
                size="icon"
                onPress={handlePrevMonth}
                disabled={!canGoPrevMonth}
              >
                <Icon name="ChevronLeft" />
              </Button>
              <Button
                testID="date-picker-next-month"
                variant="ghost"
                size="icon"
                onPress={handleNextMonth}
              >
                <Icon name="ChevronRight" />
              </Button>
            </View>
          </View>
          <Calendar
            calendarActiveDateRanges={calendarActiveDateRanges}
            calendarFirstDayOfWeek="monday"
            calendarFormatLocale={i18n.language}
            calendarMonthId={monthId}
            calendarMinDateId={minDateId}
            onCalendarDayPress={handleCalendarDayPress}
            theme={calendarTheme}
            getCalendarDayFormat={getCalendarDayFormat}
          />
        </View>
      </PopoverContent>
    </Popover>
  );
};
